'use client';

import { c as _c } from "react/compiler-runtime";
import { I18N_CONF } from '@funkey/locales';
import { useMemo } from 'react';
import { cx } from 'class-variance-authority';
import { Order, SortedSet } from 'effect';
import { Input, Toggle } from '~/components/primitives';
import { useI18n } from '~/features/i18n';
import { usePortalCtx } from '~/features/global/portal';
import { useCategoriesQuery, useRegionsQuery } from '~/features/activities/ActivityFilterBar/config/shared';
import { resolveAllowlistToggle } from '../_lib/rules';
import { useCatalog_EditMode_Session } from './session';
import { Catalog_EditMode_ModeSwitch } from './mode-switch';

/** Body of the Rules tab in the edit-mode sidebar. Sections:
 *   1. Catalog mode (radio cards + auto-add checkbox)
 *   2. Regions (multi-select chips)
 *   3. Excluded categories (chips that toggle struck-through when excluded)
 *   4. Budget caps (min/max EUR per person)
 *
 * `participants` is round-tripped in state but not surfaced — the design
 * handoff doesn't include it as a UI control. */
export const Catalog_EditMode_RulesPanel: React.FC = () => {
  const $ = _c(8);
  const i18n = useI18n();
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Catalog_EditMode_ModeSwitch />;
    t1 = <RegionsSection />;
    t2 = <ExcludedCategoriesSection />;
    t3 = <BudgetSection />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== i18n) {
    t4 = i18n._("bizz.booking.edit.rules.footerNote", undefined, {
      message: "Rules apply to every employee viewing the catalog. Click \"Save & publish\" to make changes visible."
    });
    $[4] = i18n;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <div className="flex flex-col gap-6">{t0}{t1}{t2}{t3}<p className="text-xs text-blue-400">{t4}</p></div>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
const RegionsSection: React.FC = () => {
  const $ = _c(26);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  const regionsQuery = useRegionsQuery(I18N_CONF.DEFAULT_COUNTRY);
  let t0;
  if ($[0] !== i18n.locale || $[1] !== regionsQuery.regions) {
    let t1;
    if ($[3] !== i18n.locale) {
      t1 = (a, b) => (a.name ?? "").localeCompare(b.name ?? "", i18n.locale);
      $[3] = i18n.locale;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = [...(regionsQuery.regions ?? [])].sort(t1);
    $[0] = i18n.locale;
    $[1] = regionsQuery.regions;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const sortedRegions = t0;
  let t1;
  if ($[5] !== sortedRegions) {
    t1 = sortedRegions.map(_temp);
    $[5] = sortedRegions;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const allRegionIds = t1;
  let t2;
  if ($[7] !== session) {
    t2 = next => session.setRules(prev => ({
      ...prev,
      activityFilters: {
        ...prev.activityFilters,
        activityRegionIDs: next
      }
    }));
    $[7] = session;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const allowlist = useRuleAllowlist(allRegionIds, session.rules.activityFilters.activityRegionIDs, t2);
  let t3;
  if ($[9] !== i18n) {
    t3 = i18n._("bizz.booking.edit.rules.regions.title", undefined, {
      message: "Regions"
    });
    $[9] = i18n;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <h3 className="text-sm font-semibold text-blue">{t3}</h3>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== i18n) {
    t5 = i18n._("bizz.booking.edit.rules.regions.description2", undefined, {
      message: "Every region is included by default. Click one to exclude it \u2014 struck-through regions stay out of the catalog."
    });
    $[13] = i18n;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <p className="text-xs text-blue-400">{t5}</p>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== allowlist || $[18] !== i18n || $[19] !== regionsQuery.regions || $[20] !== sortedRegions) {
    t7 = regionsQuery.regions ? <ul className="grid grid-cols-2 gap-2">{sortedRegions.map(region => {
        const regionId = region.id;
        return <li key={region.id}><RuleChip pressed={allowlist.isActive(regionId)} onPressedChange={() => allowlist.toggle(regionId)} className="w-full justify-start">{region.name}</RuleChip></li>;
      })}</ul> : <p className="text-xs text-blue-400">{i18n._("Loading\u2026")}</p>;
    $[17] = allowlist;
    $[18] = i18n;
    $[19] = regionsQuery.regions;
    $[20] = sortedRegions;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t4 || $[23] !== t6 || $[24] !== t7) {
    t8 = <section className="flex flex-col gap-2" data-testid="bizz-booking-rules-regions">{t4}{t6}{t7}</section>;
    $[22] = t4;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};
const ExcludedCategoriesSection: React.FC = () => {
  const $ = _c(27);
  const i18n = useI18n();
  const portal = usePortalCtx("BIZZ");
  const session = useCatalog_EditMode_Session();
  let t0;
  if ($[0] !== portal.bizz.tenantId) {
    t0 = {
      tenantId: portal.bizz.tenantId
    };
    $[0] = portal.bizz.tenantId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const categoriesQuery = useCategoriesQuery(t0);
  let t1;
  if ($[2] !== categoriesQuery.categories) {
    t1 = categoriesQuery.categories ?? [];
    $[2] = categoriesQuery.categories;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = t1.filter(_temp2);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const rootCategories = t2;
  let t3;
  if ($[6] !== rootCategories) {
    t3 = rootCategories.map(_temp3);
    $[6] = rootCategories;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const allCategoryIds = t3;
  let t4;
  if ($[8] !== session) {
    t4 = next => session.setRules(prev => ({
      ...prev,
      activityFilters: {
        ...prev.activityFilters,
        rootCategoryIDs: next
      }
    }));
    $[8] = session;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const allowlist = useRuleAllowlist(allCategoryIds, session.rules.activityFilters.rootCategoryIDs, t4);
  let t5;
  if ($[10] !== i18n) {
    t5 = i18n._("bizz.booking.edit.rules.excludedCategories.title2", undefined, {
      message: "Categories"
    });
    $[10] = i18n;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <h3 className="text-sm font-semibold text-blue">{t5}</h3>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== i18n) {
    t7 = i18n._("bizz.booking.edit.rules.excludedCategories.description2", undefined, {
      message: "Every category is included by default. Click one to exclude it \u2014 struck-through categories stay out of the catalog."
    });
    $[14] = i18n;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <p className="text-xs text-blue-400">{t7}</p>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== allowlist || $[19] !== categoriesQuery.categories || $[20] !== i18n || $[21] !== rootCategories) {
    t9 = categoriesQuery.categories ? <ul className="flex flex-wrap gap-2">{rootCategories.map(category_1 => {
        const categoryId = category_1.id;
        return <li key={category_1.id}><RuleChip pressed={allowlist.isActive(categoryId)} onPressedChange={() => allowlist.toggle(categoryId)}>{category_1.filterName ?? category_1.name}</RuleChip></li>;
      })}</ul> : <p className="text-xs text-blue-400">{i18n._("Loading\u2026")}</p>;
    $[18] = allowlist;
    $[19] = categoriesQuery.categories;
    $[20] = i18n;
    $[21] = rootCategories;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t6 || $[24] !== t8 || $[25] !== t9) {
    t10 = <section className="flex flex-col gap-2" data-testid="bizz-booking-rules-categories">{t6}{t8}{t9}</section>;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};

/** Allowlist-style rule toggle with `null = all active` semantics. Both the
 * regions and excluded-categories sections store their rules as
 * `readonly number[] | null`, where `null` means "no constraint = every item
 * active". This hook exposes:
 *   - `isActive(id)` — true if `current` is `null` (all) or contains `id`
 *   - `toggle(id)` — delegates the next-value decision to
 *     `resolveAllowlistToggle`.
 *
 * `allIds` should be a stable reference (memoised by the caller) — the
 * `useMemo` here depends on it. */
function useRuleAllowlist(allIds, current, onChange) {
  const $ = _c(13);
  const isAllActive = current === null;
  let t0;
  if ($[0] !== allIds || $[1] !== current || $[2] !== isAllActive) {
    t0 = SortedSet.fromIterable(isAllActive ? allIds : current, Order.number);
    $[0] = allIds;
    $[1] = current;
    $[2] = isAllActive;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const selectedSet = t0;
  let t1;
  if ($[4] !== selectedSet) {
    t1 = id => SortedSet.has(selectedSet, id);
    $[4] = selectedSet;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== allIds || $[7] !== current || $[8] !== onChange) {
    t2 = id_0 => onChange(resolveAllowlistToggle(allIds, current, id_0));
    $[6] = allIds;
    $[7] = current;
    $[8] = onChange;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1 || $[11] !== t2) {
    t3 = {
      isActive: t1,
      toggle: t2
    };
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}

/** Rule chip: a Toggle styled `secondary-50` (green) when active and
 * strike-through when not. Wraps the shared `Toggle` primitive so both rule
 * sections look identical without each section repeating the `line-through`
 * conditional. */
const RuleChip: React.FC<{
  pressed: boolean;
  onPressedChange: () => void;
  className?: string;
  children: React.ReactNode;
}> = props => {
  const $ = _c(7);
  const t0 = props.pressed;
  const t1 = props.onPressedChange;
  let t2;
  if ($[0] !== props.className) {
    t2 = cx("data-[state=off]:line-through data-[state=off]:opacity-60", props.className);
    $[0] = props.className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== props.children || $[3] !== props.onPressedChange || $[4] !== props.pressed || $[5] !== t2) {
    t3 = <Toggle pressed={t0} onPressedChange={t1} className={t2}>{props.children}</Toggle>;
    $[2] = props.children;
    $[3] = props.onPressedChange;
    $[4] = props.pressed;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const BudgetSection: React.FC = () => {
  const $ = _c(32);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  const portal = usePortalCtx("BIZZ");
  let t0;
  if ($[0] !== i18n) {
    t0 = i18n._("bizz.booking.edit.rules.budget.title", undefined, {
      message: "Budget caps"
    });
    $[0] = i18n;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <h3 className="text-sm font-semibold text-blue">{t0}</h3>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== i18n || $[5] !== portal.bizz.vatInclusive) {
    t2 = portal.bizz.vatInclusive ? i18n._("bizz.booking.edit.rules.budget.description.vatIncl", undefined, {
      message: "Min / max budget per person (EUR, price incl. VAT). Leave empty for no cap."
    }) : i18n._("bizz.booking.edit.rules.budget.description.vatExcl", undefined, {
      message: "Min / max budget per person (EUR, price excl. VAT). Leave empty for no cap."
    });
    $[4] = i18n;
    $[5] = portal.bizz.vatInclusive;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <p className="text-xs text-blue-400">{t2}</p>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== i18n) {
    t4 = i18n._("Min");
    $[9] = i18n;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = session.rules.activityFilters.budget.min ?? "";
  let t6;
  if ($[11] !== session) {
    t6 = e => {
      const raw = e.target.value;
      session.setRules(prev => ({
        ...prev,
        activityFilters: {
          ...prev.activityFilters,
          budget: {
            ...prev.activityFilters.budget,
            min: raw === "" ? null : Number(raw)
          }
        }
      }));
    };
    $[11] = session;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Input type="number" min={0} placeholder={t4} prefix={"\u20AC"} value={t5} onChange={t6} />;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== i18n) {
    t8 = i18n._("Max");
    $[17] = i18n;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = session.rules.activityFilters.budget.max ?? "";
  let t10;
  if ($[19] !== session) {
    t10 = e_0 => {
      const raw_0 = e_0.target.value;
      session.setRules(prev_0 => ({
        ...prev_0,
        activityFilters: {
          ...prev_0.activityFilters,
          budget: {
            ...prev_0.activityFilters.budget,
            max: raw_0 === "" ? null : Number(raw_0)
          }
        }
      }));
    };
    $[19] = session;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8 || $[23] !== t9) {
    t11 = <Input type="number" min={0} placeholder={t8} prefix={"\u20AC"} value={t9} onChange={t10} />;
    $[21] = t10;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t7) {
    t12 = <div className="grid grid-cols-2 gap-2">{t7}{t11}</div>;
    $[25] = t11;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t1 || $[29] !== t12 || $[30] !== t3) {
    t13 = <section className="flex flex-col gap-2" data-testid="bizz-booking-rules-budget">{t1}{t3}{t12}</section>;
    $[28] = t1;
    $[29] = t12;
    $[30] = t3;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};
function _temp(r) {
  return r.id;
}
function _temp2(category) {
  return category.isRoot;
}
function _temp3(category_0) {
  return category_0.id;
}